import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteVendor,
    getVendors,
    updateVendorStatus,
} from '../store/vendors/actions';
import { capitalizeFirst } from '../commonUtils/string.helper';
import { hasPermission } from '../commonUtils/permission.helper';
import constants from '../commonUtils/constants';
import { Switch } from "../components/ui/switch";
import IconButton from '../components/IconButton';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

const VendorsContext = createContext();

export const useVendors = () => useContext(VendorsContext);

export const VendorsProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { filters, VendorsList, loading, errors } = useSelector((state) => state.Vendors);

    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");
    const [updateData, setUpdateData] = useState({ _id: "", title: "" });

    const debouncedGetVendors = useMemo(() => debounce((params) => {
        dispatch(getVendors(params));
    }, 300), [dispatch]);

    useEffect(() => {
        debouncedGetVendors({
            currentPage: 1,
            perPage: VendorsList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            ...filters,
        });
    }, [filters, VendorsList?.pagination?.perPage, debouncedGetVendors]);

    useEffect(() => {
        handleCloseModal('update');
        handleCloseModal('delete');
    }, [VendorsList]);

    const vendors = useMemo(() => VendorsList?.data?.map(
        ({ _id, key, title, status }, index) => ({
            id: index + 1,
            _id,
            key,
            title,
            status: capitalizeFirst(status),
        })
    ), [VendorsList]);

    const pagination = useMemo(() => VendorsList?.pagination, [VendorsList]);

    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetVendors({ currentPage, perPage, ...filters });
    }, [debouncedGetVendors, filters]);

    const editVendor = useCallback((data) => setUpdateData(data), []);
    const handleToggleStatus = useCallback((id) => dispatch(updateVendorStatus(id)), [dispatch]);
    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const columnHelper = useMemo(() => createColumnHelper(), []);
    const columns = useMemo(() => [
        columnHelper.accessor('id', { header: '#', cell: info => info.getValue() }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('title', { header: 'Title', cell: info => info.getValue() }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: info => (
                <Switch
                    checked={info.getValue() === 'Active'}
                    onCheckedChange={() => handleToggleStatus(info.row.original._id)}
                    disabled={!hasPermission('vendors.update')}
                />
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    {hasPermission('vendors.update') && (
                        <IconButton variant="action_default" size="icon" Icon={PencilSquareIcon} onClick={() => editVendor(info.row.original)} />
                    )}
                    {hasPermission('vendors.create_delete') && (
                        <IconButton variant="action_default" size="icon" Icon={TrashIcon} onClick={() => askDeleteConfirmation(info.row.original._id)} />
                    )}
                </>
            ),
        }),
    ], [columnHelper, handleToggleStatus, editVendor, askDeleteConfirmation]);

    const table = useReactTable({
        data: vendors,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageIndex: VendorsList?.pagination?.currentPage - 1,
                pageSize: VendorsList?.pagination?.perPage,
            },
        },
        manualPagination: true,
        pageCount: VendorsList?.pagination?.totalPages,
    });

    const handleCloseModal = (action) => {
        if(action === "update"){
            setUpdateData({id:"", title:""})
            setOpenUpdateModal(false);
        } else if(action === "delete") {
            setToDeleteId("");
            setShowDeleteConfirmationDialog(false);
        }
    }
    const handleDelete = () => {
        dispatch(deleteVendor(toDeleteId));
        handleCloseModal('delete');
    };

    return (
        <VendorsContext.Provider value={{
            vendors,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editVendor,
            openUpdateModal,
            setOpenUpdateModal,
            updateData,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
        }}>
            {children}
        </VendorsContext.Provider>
    );
};
